// import "@babel/polyfill";

import CONST from './app/config/CONST';
import DEFAULTS from './app/config/defaults';

import React from 'react';
import {createRoot} from "react-dom/client";
import store from './app/store/store';
import {Provider} from 'react-redux';
import App from './app/App';

import {logInfo, logWarn, logErr, setLog} from "./app/helpers/debugLog";
import GATracker from "./app/helpers/GATracker";
import HandleInstallmentPreviewWidgetPortals from "./app/components/HandleInstallmentPreviewWidgetPortals/HandleInstallmentPreviewWidgetPortals";


const importDevelopmentCSS = () => {
    if(process.env.REACT_APP_ENV === 'development') {
        return import(`./styles/themes/${process.env.REACT_APP_THEME}/theme.scss`);
    }
    return true;
}
await importDevelopmentCSS();

window[CONST.JS_NAMESPACE] = ((window, document) => {

    window[CONST.JS_NAMESPACE+"_debug"] = false;
    window[CONST.JS_NAMESPACE+"_initialized"] = false;
    window[CONST.JS_NAMESPACE+"_jsApiReady"] = false;
    window[CONST.JS_NAMESPACE+"_queue"] = [];
    let tpConfigParams = {};
    let styleLoaded = false;
    let styleElt = null;
    let appMountPoint;

    let appRootEl = document.createElement('div');
    let appRoot = createRoot(appRootEl);

    const requiredConfigParameters = ['lang','marketCode','source','cookies'];
    const optionalConfigParameters = ['theme'];

    const injectStyle = (theme) => {
        const styleHref = `${CONST.APP_BASEURL}/static/css/${theme}/theme.min.css?v=${CONST.VERSION}`;
        if (!document.body.classList.contains(CONST.CSS_NAMESPACE+'style-'+theme+'-loaded')) {
            logInfo(`Loading style [${styleHref}] ...`);
            const head = document.getElementsByTagName('head')[0];
            styleElt = document.createElement('link');
            styleElt.href = styleHref;
            styleElt.type = 'text/css';
            styleElt.rel = 'stylesheet';
            head.appendChild(styleElt);
            styleElt.addEventListener('load', () => onStyleLoaded(theme));
        }
        else {
            logInfo(`Style [${styleHref}] already loaded`);
        }
    }

    const onStyleLoaded = (theme) => {
        styleLoaded = true;
        logInfo('Style loaded');
        document.body.classList.add(CONST.CSS_NAMESPACE+'style-loaded', CONST.CSS_NAMESPACE+'style-'+theme+'-loaded');
    }

    const validateTpConfigParams = () => {

        const errors = [];
        const warnings = [];

        requiredConfigParameters.forEach( (requiredParameter) => {
            if(!tpConfigParams[requiredParameter]) {
                errors.push(`Required config parameter missing [${requiredParameter}]`);
            }
        });
        optionalConfigParameters.forEach( (optionalParameter) => {
            if(!tpConfigParams[optionalParameter]) {
                warnings.push(`You did not specified [${optionalParameter}] parameter. Default will be applied.`);
            }
        });

        warnings.forEach((warn) => {
            logWarn(warn);
        });

        if(errors.length === 0) {
            return true;
        }
        else {
            errors.forEach((err) => {
                logErr(err);
            });
        }

    }

    const mergeTpConfigToDefaults = (configParameters) => {
        return {
            ...DEFAULTS.config,
            ...configParameters
        };
    }

    const createAppMountPoint = () => {
        appMountPoint = document.createElement('div');
        appMountPoint.id = CONST.DOM_APP_MOUNTPOINT_ID + '_r' + Math.floor(Math.random() * 99999999999);
        appMountPoint.style.display = 'none';
        document.body.appendChild(appMountPoint);
    }

    const reactInit = (config) => {

        if(process.env.REACT_APP_ENABLE_PREVIEW_WIDGETS === "true") {
            logInfo("Preview widgets enabled");
        }

        appRoot.render(
            <Provider store={store}>
                <App
                    config={config}
                />
                { process.env.REACT_APP_ENABLE_PREVIEW_WIDGETS === "true" &&
                    <HandleInstallmentPreviewWidgetPortals />
                }
            </Provider>
        );

        logInfo('Application initialized with following configuration:', config);
    }

    const init = (_tpConfigParams) => {

        window[CONST.JS_NAMESPACE+"_config"] = _tpConfigParams;

        if(window[CONST.JS_NAMESPACE+"_initialized"] === true) {
            destroy();
        }

        tpConfigParams = _tpConfigParams;
        window[CONST.JS_NAMESPACE+"_debug"] = ((tpConfigParams && tpConfigParams.debug === true) || CONST.DEBUG === true);

        setLog(window[CONST.JS_NAMESPACE+"_debug"]);
        logInfo('Initializing application ...' );
        logInfo('Version:', CONST.VERSION);

        if(window[CONST.JS_NAMESPACE+"_debug"]) {
            logWarn(`Debug mode activated. Please remove [debug: true] in production!`);
        }

        if (window[CONST.JS_NAMESPACE+"_initialized"] === true) {
            logErr('You cannot initialize multiple application instances. If you want to re-init the application, first you need to destroy it with '+CONST.JS_NAMESPACE+'.destroy();');
            return;
        }

        window[CONST.JS_NAMESPACE+"_initialized"] = true;

        const validate = validateTpConfigParams();
        if(!validate) {
            destroy();
            return;
        }

        const appConfig = mergeTpConfigToDefaults(tpConfigParams);
        injectStyle(appConfig.theme || 'default');
        createAppMountPoint();
        reactInit(appConfig);

    }

    const destroy = () => {

        logInfo('Destroying application ...');

        GATracker.trackEvent({
            name: GATracker.EVENTS.APPLICATION_DESTROY.name,
            category: GATracker.EVENTS.APPLICATION_DESTROY.category,
            action: GATracker.EVENTS.APPLICATION_DESTROY.action
        });

        if (styleElt) {
            styleElt.removeEventListener('load', onStyleLoaded);
        }

        if(appMountPoint) {
            document.body.removeChild(appMountPoint);
            appMountPoint = null;
        }

        window[CONST.JS_NAMESPACE].close(true);

        cleanPreviewWidgets();

        window[CONST.JS_NAMESPACE+"_initialized"] = false;

        logInfo('Application destroyed');
    };


    const cleanPreviewWidgets = () => {
        logInfo('cleanPreviewWidgets')
        const portalContainers = document.querySelectorAll(CONST.PREVIEW_WIDGET_SELECTOR);
        portalContainers.forEach((el) => {
            el.innerHTML = '';
        });
    }

    /*
    const getHeight = (el) => {
        let h;
        try {
            h = window.getComputedStyle(el, null).getPropertyValue('height');
        } catch(e) {
            h = el.currentStyle.height;
        }
        return h;
    }
    */

    const reset = (_tpConfigParams) => {

        logInfo('Reset application ...');

        if(!_tpConfigParams) {
            _tpConfigParams = window[CONST.JS_NAMESPACE+"_config"];
        }

        /*
        const portalContainers = document.querySelectorAll(CONST.PREVIEW_WIDGET_SELECTOR);
        portalContainers.forEach((el) => {
            el.style.height = getHeight(el);
        });
        */

        // destroy();
        init(_tpConfigParams);
    }


    const open = (args) => {
        if(!window[CONST.JS_NAMESPACE+"_jsApiReady"]) {
            window[CONST.JS_NAMESPACE+"_queue"].push({'func': 'open', 'args': args});
        }
    }
    const close = (args) => {
        if(!window[CONST.JS_NAMESPACE+"_jsApiReady"]) {
            window[CONST.JS_NAMESPACE+"_queue"].push({'func': 'close', 'args': args});
        }
    }
    const update = (args) => {
        if(!window[CONST.JS_NAMESPACE+"_jsApiReady"]) {
            window[CONST.JS_NAMESPACE+"_queue"].push({'func': 'update', 'args': args});
        }
    }

    return {
        init,
        destroy,
        reset,
        open,
        close,
        update,
        styleLoaded,
        version: CONST.VERSION
    };

})(window, document);
